import React, { useEffect, useState } from "react";
import { X, Mail, Phone, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { BRAND } from "./data";
import { useViewRouter } from "../../hooks/useViewRouter";

const FORMSPREE_ENDPOINT = "https://formspree.io/f/xnjylwwg";

const INTENTS = ["Buy", "Sell", "Invest", "Advisory", "Estates"];
const AREAS = [
  "Lutyens' Delhi",
  "South Delhi",
  "Gurugram",
  "Noida",
  "Greater Noida",
  "Faridabad",
  "Pan-India",
  "International",
];
const BUDGETS = ["₹1–5 Cr", "₹5–15 Cr", "₹15–50 Cr", "₹50 Cr+"];

const inputClass =
  "w-full bg-transparent border-b border-num-gold/30 focus:border-num-gold outline-none py-3 px-1 font-ui text-[13px] text-num-cream placeholder:text-num-cream/40 transition-colors";

const ContactModal = () => {
  const { contactModalOpen, closeContactModal } = useViewRouter();
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    intent: "Buy",
    area_of_interest: "",
    budget: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  // Close on Escape
  useEffect(() => {
    if (!contactModalOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeContactModal();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [contactModalOpen, closeContactModal]);

  if (!contactModalOpen) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.first_name || !form.last_name || !form.phone || !form.email) {
      toast.error("Please complete the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const data = new FormData();
      Object.entries({ ...form, source_section: "consult_modal" }).forEach(([k, v]) => data.append(k, v));
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        body: data,
        headers: { Accept: "application/json" },
      });
      if (!res.ok) throw new Error("Formspree error");
      toast.success("Enquiry received. An advisor will reach out within one business day.");
      closeContactModal();
      setForm({
        first_name: "",
        last_name: "",
        phone: "",
        email: "",
        intent: "Buy",
        area_of_interest: "",
        budget: "",
        message: "",
      });
    } catch (err) {
      console.error(err);
      toast.error("We could not submit your enquiry. Please try again or call directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="view-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Consult the House — enquiry"
      data-testid="contact-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget) closeContactModal();
      }}
    >
      <div className="relative w-full max-w-[820px] max-h-[90vh] overflow-y-auto bg-num-deep border border-num-gold/25 mx-4 p-7 lg:p-10"
        style={{ boxShadow: "0 30px 80px -20px rgba(0,0,0,0.55)" }}
      >
        {/* Close */}
        <button
          type="button"
          onClick={closeContactModal}
          className="absolute top-5 right-5 text-num-cream/70 hover:text-num-gold transition-colors"
          aria-label="Close enquiry"
          data-testid="contact-modal-close"
        >
          <X size={22} />
        </button>

        {/* Header */}
        <div className="text-center max-w-[480px] mx-auto mb-8">
          <div className="font-ui text-[9px] uppercase track-eyebrow text-num-gold mb-4">
            Consult the House
          </div>
          <h3
            className="font-display font-medium text-num-cream leading-[1.05]"
            style={{ fontSize: "clamp(28px, 3.4vw, 40px)" }}
          >
            Speak to{" "}
            <span className="font-italic italic font-normal text-num-gold-light">an Advisor</span>
          </h3>
          <p className="font-body italic text-[13px] mt-3" style={{ color: "rgba(247,243,236,0.55)" }}>
            Our advisors are available six days a week — entirely at your pace, entirely in
            confidence.
          </p>
        </div>

        <form onSubmit={onSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
          <div>
            <label className="font-ui text-[9px] uppercase track-eyebrow text-num-gold/80">First Name *</label>
            <input required type="text" value={form.first_name} onChange={set("first_name")} className={inputClass} placeholder="Rajiv" data-testid="modal-first-name" />
          </div>
          <div>
            <label className="font-ui text-[9px] uppercase track-eyebrow text-num-gold/80">Last Name *</label>
            <input required type="text" value={form.last_name} onChange={set("last_name")} className={inputClass} placeholder="Malhotra" data-testid="modal-last-name" />
          </div>
          <div>
            <label className="font-ui text-[9px] uppercase track-eyebrow text-num-gold/80">Phone *</label>
            <input required type="tel" value={form.phone} onChange={set("phone")} className={inputClass} placeholder="+91 98100 00000" data-testid="modal-phone" />
          </div>
          <div>
            <label className="font-ui text-[9px] uppercase track-eyebrow text-num-gold/80">Email *</label>
            <input required type="email" value={form.email} onChange={set("email")} className={inputClass} placeholder="rajiv@example.com" data-testid="modal-email" />
          </div>

          <div className="sm:col-span-2">
            <label className="font-ui text-[9px] uppercase track-eyebrow text-num-gold/80">I am looking to</label>
            <div className="flex flex-wrap gap-2 mt-3">
              {INTENTS.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, intent: opt }))}
                  className={`font-ui text-[10px] uppercase track-label px-4 py-2 border transition-colors ${
                    form.intent === opt
                      ? "border-num-gold bg-num-gold text-num-deep"
                      : "border-num-gold/30 text-num-cream/70 hover:border-num-gold hover:text-num-cream"
                  }`}
                  data-testid={`modal-intent-${opt.toLowerCase()}`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="font-ui text-[9px] uppercase track-eyebrow text-num-gold/80">Area of Interest</label>
            <select value={form.area_of_interest} onChange={set("area_of_interest")} className={`${inputClass} appearance-none bg-num-deep`} data-testid="modal-area">
              <option value="">Select area</option>
              {AREAS.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div>
            <label className="font-ui text-[9px] uppercase track-eyebrow text-num-gold/80">Budget · Ticket Size</label>
            <select value={form.budget} onChange={set("budget")} className={`${inputClass} appearance-none bg-num-deep`} data-testid="modal-budget">
              <option value="">Select budget</option>
              {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>

          <div className="sm:col-span-2">
            <label className="font-ui text-[9px] uppercase track-eyebrow text-num-gold/80">Message — Optional</label>
            <textarea
              rows={3}
              value={form.message}
              onChange={set("message")}
              className={`${inputClass} resize-none border border-num-gold/20 focus:border-num-gold p-3`}
              placeholder="Tell us a little about what you are looking for…"
              data-testid="modal-message"
            />
          </div>

          <div className="sm:col-span-2 mt-3">
            <button
              type="submit"
              disabled={submitting}
              className="w-full btn-gold py-4 font-ui text-[11px] uppercase track-label disabled:opacity-60 inline-flex items-center justify-center gap-2"
              data-testid="modal-submit"
            >
              {submitting && <Loader2 size={14} className="animate-spin" />}
              Send Enquiry
            </button>
          </div>
        </form>

        {/* Quick contact strip */}
        <div className="mt-7 pt-5 border-t border-num-gold/15 grid grid-cols-1 sm:grid-cols-3 gap-3 text-num-cream/70">
          <a href={`tel:${BRAND.phones[0].replace(/\s+/g, "")}`} className="flex items-center gap-2 font-ui text-[11px] hover:text-num-gold">
            <Phone size={14} className="text-num-gold" /> {BRAND.phones[0]}
          </a>
          <a href={`mailto:${BRAND.email}`} className="flex items-center gap-2 font-ui text-[11px] hover:text-num-gold">
            <Mail size={14} className="text-num-gold" /> {BRAND.email}
          </a>
          <span className="flex items-center gap-2 font-ui text-[11px]">
            <Clock size={14} className="text-num-gold" /> {BRAND.hours}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ContactModal;
